import type { NextPage } from "next";
import React, { useState, useEffect } from "react";
import { Formik, Form, FormikErrors } from "formik";
import { useRouter } from "next/router";
import validator from "validator";
import type { registerInput } from "../types/register";
import { Loading } from "../components/loading";
import { GetAuth, SetAuth } from "../lib/auth";
import { useRegisterMutation } from "../lib/graphql/register.graphql";
import { passwordOptions } from "../lib/passwordOptions";
import toast from "react-hot-toast";
import {
	Center,
	CenterWithLogo,
	ContainerWhite,
	FlexCenter,
	SplitLine,
	WhiteBox,
	WhiteLink,
} from "../components/basic";
import { Field, Submit } from "../components/form";
import { Pages } from "../lib/pages";

const Login: NextPage = () => {
	const router = useRouter();
	const [registerMutation] = useRegisterMutation();
	const [initialValues, setInitialValues] = useState<registerInput>({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
	});
	const [loaded, setLoaded] = useState(false);

	const validate = (values: registerInput) => {
		const errors: FormikErrors<registerInput> = {};
		if (!validator.isEmail(values.email)) errors.email = "Špatný formát emailu";
		if (!validator.isAlphanumeric(values.firstName, "cs-CZ"))
			errors.firstName = "Špatný formát jména";
		if (!validator.isAlphanumeric(values.lastName, "cs-CZ"))
			errors.lastName = "Špatný formát příjmení";
		if (!validator.isStrongPassword(values.password, passwordOptions))
			errors.password = "Heslo není dostatečně silné";
		if (values.password !== values.confirmPassword)
			errors.confirmPassword = "Hesla se neshodují";
		return errors;
	};

	const submit = async (values: registerInput) => {
		setInitialValues(values);
		try {
			setLoaded(false);
			const { data } = await registerMutation({
				variables: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					password: values.password,
				},
			});
			SetAuth("Bearer " + data.register);
			router.reload();
		} catch (error) {
			setLoaded(true);
			toast.error(error.message);
		}
	};

	useEffect(() => {
		if (GetAuth()) router.push(Pages.Index);
		else setLoaded(true);
	}, [router]);

	if (!loaded)
		return (
			<ContainerWhite>
				<Center>
					<Loading />
				</Center>
			</ContainerWhite>
		);

	return (
		<ContainerWhite>
			<CenterWithLogo>
				<WhiteBox>
					<Formik
						initialValues={initialValues}
						onSubmit={submit}
						validate={validate}>
						{({ errors, touched, isValid }) => (
							<Form>
								<FlexCenter>
									<Field
										name="firstName"
										placeholder="Jméno"
										type="text"
										errors={errors.firstName}
										touched={touched.firstName}
									/>

									<Field
										name="lastName"
										placeholder="Příjmení"
										type="text"
										errors={errors.lastName}
										touched={touched.lastName}
									/>

									<Field
										name="email"
										placeholder="Email"
										type="email"
										errors={errors.email}
										touched={touched.email}
									/>

									<Field
										name="password"
										placeholder="Heslo"
										type="password"
										errors={errors.password}
										touched={touched.password}
									/>

									<Field
										name="confirmPassword"
										placeholder="Heslo znovu"
										type="password"
										errors={errors.confirmPassword}
										touched={touched.confirmPassword}
									/>

									<Submit isValid={isValid}>Registrovat se</Submit>
								</FlexCenter>
							</Form>
						)}
					</Formik>
					<SplitLine />
					<WhiteLink href={Pages.Login}>Přihlásit se</WhiteLink>
				</WhiteBox>
			</CenterWithLogo>
		</ContainerWhite>
	);
};

export default Login;
